/**
 * Strategy session application form.
 *
 * Collects the details needed to evaluate fit — who they are, what they're
 * exploring, timeline, investment range, decision authority, the situation,
 * and the outcome they want — and POSTs them to `VITE_STRATEGY_SESSION_URL`.
 * Inline validation, honeypot spam trap, explicit success/error states.
 */
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { trackCTA, trackConversion } from "@/lib/tracking";
import { trackEvent } from "@/lib/tracking";
import { storeStrategySessionSubmission } from "@/lib/lead-storage";
import { syncLeadToCrm } from "@/lib/crm-sync";

import {
  trackFormStart,
  fieldTracker,
  trackFormValidationError,
  trackFormSubmit,
  trackFormSuccess,
  trackFormError,
} from "@/lib/form-analytics";
import { attributionProps, leadAttributionPayload } from "@/lib/utm";
import { attributionCoverage, newCorrelationId } from "@/lib/lead-attribution-quality";
import {
  AccessibleField,
  FormErrorSummary,
  focusFirstError,
} from "@/components/site/form-a11y";
import {
  BUDGET_OPTIONS,
  DECISION_ROLE_OPTIONS,
  FOCUS_OPTIONS,
  TIMELINE_OPTIONS,
  strategySessionSchema,
  submitStrategySession,
  type StrategySessionInput,
} from "@/lib/strategy-session";

type Status = "idle" | "submitting" | "success" | "error";
type FieldErrors = Partial<Record<keyof StrategySessionInput, string>>;

const FIELD_ORDER = [
  "fullName",
  "email",
  "phone",
  "organization",
  "role",
  "focus",
  "timeline",
  "budget",
  "decisionRole",
  "situation",
  "outcome",
];
const FIELD_LABELS: Record<string, string> = {
  fullName: "Full name",
  email: "Email",
  phone: "Phone",
  organization: "Organization",
  role: "Your role",
  focus: "What are you exploring?",
  timeline: "Timeline",
  budget: "Investment range",
  decisionRole: "Who approves this?",
  situation: "The situation",
  outcome: "What does success look like?",
};


const EMPTY: StrategySessionInput = {
  fullName: "",
  email: "",
  phone: "",
  organization: "",
  role: "",
  focus: "",
  timeline: "",
  budget: "",
  decisionRole: "",
  situation: "",
  outcome: "",
};

export function StrategySessionApplicationForm({
  id = "apply",
  source = "strategy_session_page",
}: {
  id?: string;
  source?: string;
}) {
  const [values, setValues] = useState<StrategySessionInput>(EMPTY);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState<string | null>(null);
  const honeypot = useRef<HTMLInputElement>(null);
  const startedAt = useRef(Date.now());
  const navigate = useNavigate();

  const formIdentity = { formId: id, formName: "strategy_session_application" };
  const fields = useMemo(() => fieldTracker(formIdentity, { source }), [id, source]);

  const set =
    (key: keyof StrategySessionInput) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      trackFormStart(formIdentity, { source, first_field: key });
      setValues((v) => ({ ...v, [key]: e.target.value }));
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    };

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === "submitting") return;
    setFormError(null);

    // Bots get a silent success so they don't retry.
    if (honeypot.current?.value) {
      setStatus("success");
      return;
    }

    const parsed = strategySessionSchema.safeParse(values);
    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof StrategySessionInput;
        if (!next[key]) next[key] = issue.message;
      }
      setErrors(next);
      trackEvent("strategy_session_invalid", { fields: Object.keys(next).join(",") });
      trackFormValidationError(formIdentity, next as Record<string, string | undefined>, { source });
      focusFirstError(id, FIELD_ORDER, next);
      return;
    }

    setStatus("submitting");
    trackCTA("strategy_session_submit", { source, focus: parsed.data.focus });
    trackFormSubmit(formIdentity, { source, focus: parsed.data.focus });

    const correlationId = newCorrelationId("ss");

    try {
      const payload = {
        ...parsed.data,
        correlation_id: correlationId,
        email: parsed.data.email.toLowerCase(),
        source,
        page: typeof window !== "undefined" ? window.location.pathname : "/strategy-session",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone?.slice(0, 64) || null,
        time_on_form_ms: Date.now() - startedAt.current,
        submitted_at: new Date().toISOString(),
        ...leadAttributionPayload(),
      };
      await submitStrategySession(payload);
      const coverage = attributionCoverage(payload);
      // Persist a copy for /admin/leads — never blocks the user. The row id is
      // emitted separately so the success event isn't delayed by the insert.
      void storeStrategySessionSubmission(payload).then((leadId) => {
        trackEvent("lead_recorded", {
          form_name: formIdentity.formName,
          correlation_id: correlationId,
          lead_id: leadId ?? "merged_or_unavailable",
          ...coverage,
        });
      });
      syncLeadToCrm({
        event: "strategy_session_application",
        email: parsed.data.email,
        fullName: parsed.data.fullName,
        phone: parsed.data.phone,
        organization: parsed.data.organization,
        role: parsed.data.role,
        focus: parsed.data.focus,
        timeline: parsed.data.timeline,
        budget: parsed.data.budget,
        decisionAuthority: parsed.data.decisionRole,
        source: source ?? "strategy_session_form",
      });

      setStatus("success");
      setValues(EMPTY);
      trackConversion("strategy_session_submit", {
        source,
        correlation_id: correlationId,
        ...coverage,
        focus: parsed.data.focus,
        timeline: parsed.data.timeline,
        budget: parsed.data.budget,
      });
      trackFormSuccess(formIdentity, {
        source,
        correlation_id: correlationId,
        ...coverage,
        ...attributionProps(),
        focus: parsed.data.focus,
        timeline: parsed.data.timeline,
        budget: parsed.data.budget,
        time_on_form_ms: Date.now() - startedAt.current,
      });
      navigate("/strategy-session/thank-you");
    } catch (err) {
      setStatus("error");
      const message = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setFormError(message);
      trackEvent("strategy_session_error", { message });
      trackFormError(formIdentity, message, { source });
    }
  }

  if (status === "success") {
    return (
      <div
        id={id}
        role="status"
        aria-live="polite"
        tabIndex={-1}
        ref={(el) => el?.focus()}
        className="border border-gold/40 bg-gold/5 p-8 text-center focus:outline-none"
      >
        <CheckCircle2 className="h-8 w-8 text-gold mx-auto" aria-hidden="true" />
        <h3 className="font-display uppercase text-white text-xl mt-4">Application received</h3>
        <p className="text-silver text-sm mt-3 max-w-md mx-auto leading-relaxed">
          We review every application personally. If it's a fit, you'll get a
          reply within one business day with strategy session times. Watch your
          inbox — and check spam just in case.
        </p>
        <button
          type="button"
          onClick={() => {
            startedAt.current = Date.now();
            setStatus("idle");
          }}
          className="mt-6 text-xs uppercase tracking-[0.2em] text-gold hover:underline"
        >
          Submit another application
        </button>
      </div>
    );
  }

  return (
    <form
      id={id}
      onSubmit={onSubmit}
      {...fields}
      noValidate
      aria-labelledby={`${id}-title`}
      aria-busy={status === "submitting"}
      className="border border-gold/20 bg-black/30 p-6 md:p-8"
    >
      <h3 id={`${id}-title`} className="font-display uppercase text-white text-xl">
        Apply for a strategy session
      </h3>
      <p className="text-silver text-sm mt-2">
        Selective intake. The more context you give, the faster we can confirm fit.
      </p>

      <FormErrorSummary formId={id} order={FIELD_ORDER} errors={errors} labels={FIELD_LABELS} />

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <AccessibleField formId={id} name="fullName" label="Full name" required error={errors.fullName}>
          {(p) => <input {...p} type="text" autoComplete="name" value={values.fullName} onChange={set("fullName")} className="input-dark" placeholder="Jordan Reeves" />}
        </AccessibleField>
        <AccessibleField formId={id} name="email" label="Email" required error={errors.email}>
          {(p) => <input {...p} type="email" autoComplete="email" value={values.email} onChange={set("email")} className="input-dark" placeholder="you@company.com" />}
        </AccessibleField>
        <AccessibleField formId={id} name="phone" label="Phone (optional)" error={errors.phone}>
          {(p) => <input {...p} type="tel" autoComplete="tel" value={values.phone} onChange={set("phone")} className="input-dark" placeholder="(210) 555-0147" />}
        </AccessibleField>
        <AccessibleField formId={id} name="organization" label="Organization (optional)" error={errors.organization}>
          {(p) => <input {...p} type="text" autoComplete="organization" value={values.organization} onChange={set("organization")} className="input-dark" placeholder="District, church, or company" />}
        </AccessibleField>
        <AccessibleField formId={id} name="role" label="Your role (optional)" error={errors.role}>
          {(p) => <input {...p} type="text" autoComplete="organization-title" value={values.role} onChange={set("role")} className="input-dark" placeholder="Superintendent, Founder, Parent…" />}
        </AccessibleField>
        <AccessibleField formId={id} name="focus" label="What are you exploring?" required error={errors.focus}>
          {(p) => (
            <select {...p} value={values.focus} onChange={set("focus")} className="input-dark">
              <option value="">Select one…</option>
              {FOCUS_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          )}
        </AccessibleField>
        <AccessibleField formId={id} name="timeline" label="Timeline" required error={errors.timeline}>
          {(p) => (
            <select {...p} value={values.timeline} onChange={set("timeline")} className="input-dark">
              <option value="">Select one…</option>
              {TIMELINE_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          )}
        </AccessibleField>
        <AccessibleField formId={id} name="budget" label="Investment range" required error={errors.budget}>
          {(p) => (
            <select {...p} value={values.budget} onChange={set("budget")} className="input-dark">
              <option value="">Select one…</option>
              {BUDGET_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          )}
        </AccessibleField>
        <AccessibleField formId={id} name="decisionRole" label="Who approves this?" required error={errors.decisionRole}>
          {(p) => (
            <select {...p} value={values.decisionRole} onChange={set("decisionRole")} className="input-dark">
              <option value="">Select one…</option>
              {DECISION_ROLE_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          )}
        </AccessibleField>
      </div>

      <div className="mt-4 grid gap-4">
        <AccessibleField formId={id} name="situation" label="The situation" required error={errors.situation}>
          {(p) => <textarea {...p} rows={4} value={values.situation} onChange={set("situation")} className="input-dark resize-y" placeholder="What's happening right now, and what have you already tried?" />}
        </AccessibleField>
        <AccessibleField formId={id} name="outcome" label="What does success look like?" required error={errors.outcome}>
          {(p) => <textarea {...p} rows={3} value={values.outcome} onChange={set("outcome")} className="input-dark resize-y" placeholder="The outcome that would make this worth it." />}
        </AccessibleField>
      </div>

      {/* Honeypot — hidden from humans and assistive tech. */}
      <input
        ref={honeypot}
        type="text"
        name="company_website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute left-[-9999px] h-0 w-0 opacity-0"
      />

      {formError && (
        <p
          role="alert"
          tabIndex={-1}
          ref={(el) => el?.focus()}
          className="mt-5 flex items-start gap-2 border border-destructive/40 bg-destructive/10 p-3 text-sm text-white focus:outline-none"
        >
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-destructive" aria-hidden="true" />
          <span>{formError}</span>
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-6 py-4 shadow-cta hover:brightness-110 transition-all disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        {status === "submitting" ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Sending your application…
          </>
        ) : (
          <>
            Apply for a Strategy Session <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </>
        )}
      </button>

      <p aria-live="polite" className="sr-only">
        {status === "submitting" ? "Sending your application" : ""}
      </p>

      <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-silver text-center">
        Selective intake · 1 business-day response · Strategy, not sales
      </p>
    </form>
  );
}

